<script setup lang="ts">
import CustomModal from "@/components/CustomModal/CustomModal.vue";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import CustomButton from "@/components/Button/Button.vue";
import { useI18n } from "vue-i18n";
import {CrewMember} from "@/models/CrewMember";
import {getCrewList} from "@/services/crew.service";
import { useTripsStore } from "@/stores/tripsStore";
import UserInfo from "@/components/UserInfo/UserInfo.vue";
import {useCrewStore} from "@/stores/crewStore";
import { useRouter } from "vue-router";
import {useUserStore} from "@/stores/userStore";


const tripStore = useTripsStore();
const { t } = useI18n();

const { open } = defineProps({
  open: {
    type: Boolean,
    required: true
  }
});

const emit = defineEmits(['on-close', 'on-add-new-crew-list']);


const invitees = ref<{ email: string; uuid: string }[]>([]);

const crewList = ref<CrewMember[]>([]);
const status = ref<"loading" | "success" | "error">("loading");
const crewStore = useCrewStore();
const userStore = useUserStore();

const router = useRouter();


watch(
    () => userStore.sessionRestored,
    async (restored) => {
      if (restored) {
        try {
          if (!userStore.user) {
            await userStore.fetchUserProfile();
          }
          await crewStore.fetchCrewList('');
          status.value = "success";
        } catch {
          status.value = "error";
        }
      }
    },
    {immediate: true}
);

const allContacts = computed(() =>
    crewStore.crewList.flatMap(group => group.members)
);

const searchQuery = ref('');
const suggestedMembers = computed(() => {
  if (!searchQuery.value.trim()) return [];
  return allContacts.value.filter(member =>
      member.firstName.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      member.lastName.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      member.email.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

async function inviteSelectedAndClose() {
  try {
    const crewUuids = invitees.value.map(inv => inv.uuid);
    await tripStore.updateTripCrew(tripStore.trip.uuid, crewUuids);
    console.log("Invitations sent:", invitees.value);
  } catch (e) {
    console.error("Failed to send invites", e);
  }
  emit('on-close');
}

function inviteMember(email: string) {
  const member = allContacts.value.find(m => m.email === email);
  if (member && !invitees.value.find(inv => inv.email === email)) {
    invitees.value.push({ email: member.email, uuid: member.uuid });
    console.log("invitees ", invitees.value);
  }
}

function removeMember(email: string) {
  invitees.value = invitees.value.filter(inv => inv.email !== email);
  console.log("invitees ", invitees.value);
}

function inviteGroup(group: { name: string; email: string; uuid: string }[]) {
  group.forEach(friend => {
    if (!invitees.value.find(inv => inv.email === friend.email)) {
      invitees.value.push({ email: friend.email, uuid: friend.uuid });
    }
  });
}

const width = ref(window.innerWidth);
function updateWidth() {
  width.value = window.innerWidth;
}
onMounted(() => {
  window.addEventListener('resize', updateWidth);
});

onUnmounted(() => {
  window.removeEventListener('resize', updateWidth);
});

function goToCreateCrewList() {
  emit('on-close');
  try { localStorage.setItem('crewCreateModal', '1'); } catch {}
  router.push({ path: '/my/crew-list', query: { modal: 'create' } });
}

function navigateToCrewList(uuid: string) {
  if (!uuid) return;
  emit('on-close');
  try { localStorage.removeItem('crewCreateModal'); } catch {}
  router.push({ path: `/my/crew-list/${uuid}` });
}

watch(() => open, (newValue) => {
  document.documentElement.style.overflow = newValue ? 'hidden' : 'auto';
});
</script>

<template>
  <CustomModal :show="open" @on-close="$emit('on-close')" classes="w-1/6" :custom-index="40">
    <div :class="width < 768 ? 'flex flex-col h-screen w-screen' : 'flex flex-col h-[642px] w-[560px]'">

      <div class="py-5 bg-gradient-to-l from-gradients-ghostWhite to-gradients-aliceBlue flex flex-col">
        <div class="cursor-pointer self-end mr-5" type="default" @click="$emit('on-close')">
          <img src="@/assets/icons/svg/Close_16x16.svg" :alt="t('close')" width="18" />
        </div>
        <div class="px-11">
          <div class="responsive-title-6 font-semibold">{{ t('add_crew_members') }}</div>
          <div class="mt-9 flex flex-col">
            <div class="flex flex-row justify-between items-center">
              <div class="max-h-[120px] overflow-y-auto flex flex-wrap gap-2 pr-1">
                <div
                  v-for="invitee in invitees"
                  :key="invitee.email"
                  class="responsive-body-small bg-white w-fit h-6.5 pl-2 pr-1 py-1.5 rounded-8xl flex items-center gap-2 border border-bw-lightGrey"
                >
                  <span class="truncate max-w-[170px]">{{ invitee.email }}</span>
                  <button
                    class="rounded-full hover:bg-bw-lightGrey p-1 transition-colors"
                    @click.stop.prevent="removeMember(invitee.email)"
                    :aria-label="t('remove')"
                    title="Remove"
                  >
                    <img src="@/assets/icons/svg/Close_16x16.svg" :alt="t('remove')" />
                  </button>
                </div>
              </div>
            </div>

            <!-- Text input with suggestions -->
            <div class="relative">
              <VTextField
                  variant="underlined"
                  v-model="searchQuery"
                  :placeholder="t('searchMember')"
                  class="responsive-body-small"
              />
              <div
                  v-if="suggestedMembers.length > 0"
                  class="absolute z-50 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto"
              >
                <div
                    v-for="member in suggestedMembers"
                    :key="member.name"
                    class="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    @click="inviteMember(member.email)"
                >
                  {{ member.name }} ({{ member.email }})
                </div>
              </div>
            </div>
            <CustomButton
                type="black"
                :outlined="false"
                :rounded="true"
                :disabled="invitees.length === 0"
                :class="['responsive-body-small w-fit sm:h-7 mt-5', invitees.length === 0 ? 'opacity-50 cursor-not-allowed' : '']"
                @click="inviteSelectedAndClose">
              {{ t('tripOverview.addToTrip') }}
            </CustomButton>
          </div>
        </div>
      </div>

      <!-- Scrollable Group List -->
      <div class="flex-1 overflow-y-auto bg-white px-11 py-8">
        <div v-if="crewStore.crewList.length === 0"
             class="flex justify-center items-center h-full gap-2 ">
          <div class="flex flex-col bg-background-light boxClass justify-between rounded-8xl p-10">
            <div>
              <img src="@/assets/icons/svg/Crew_40x40.svg" alt="Crew">
            </div>
            <div class=" responsive-body-large smallText font-semibold">{{ t('crewList.noCrewList') }}</div>
          </div>
          <div @click="goToCreateCrewList"
               class="flex flex-col bg-primary-blue text-white justify-center items-center rounded-8xl p-8 gap-1 cursor-pointer boxClass"
               type="primary">
            <img src="@/assets/icons/svg/Add_24x24_white.svg" alt="Plus" width="44" />
            <div class=" responsive-body-large font-semibold text-center">{{ t('crewList.addNewList') }}</div>
          </div>
        </div>
        <div v-for="group in crewStore.crewList" :key="group.uuid" class="mb-6">
          <div class="flex flex-row justify-between items-center mb-4">
            <div class="flex flex-row items-center gap-2">
              <span class="responsive-body-large font-bold">{{ group.listName }}</span>
              <div class="flex justify-between items-center">
              <CustomButton
                  type="outline-black"
                  size="outlinesmall"
                  :rounded="true"
                  class="border border-black px-4 py-1 rounded-12xl w-fit arrow-btn"
                  @click.stop="navigateToCrewList(group.uuid)"
              >
                <img
                    src="@/assets/icons/svg/Arrow_right_black_14x14.svg"
                    alt="arrow"
                    class="icon-default"
                />
                <img
                    src="@/assets/icons/svg/Arrow_right_white_14x14.svg"
                    alt="arrow hover"
                    class="icon-hover"
                />
              </CustomButton>
              </div>
              <span class="responsive-body-xsmall text-bw-darkGrey">
          ({{ group.members.length }} {{ t('members') }})
        </span>
            </div>
            <div
                class="underline cursor-pointer responsive-link-small font-semibold"
                @click="inviteGroup(group.members)"
            >
              {{ t('add_all') }}
            </div>
          </div>

          <div class="flex flex-col gap-4">
            <div
                v-for="member in group.members"
                :key="member.uuid"
                class="flex flex-row items-center justify-between"
            >
              <UserInfo
                  :member="member"
              />
<!--              <div class="responsive-body-small">{{ member.name }}</div>-->
              <!-- Invite -->
              <div v-if="!invitees.find(inv => inv.email === member.email)"
                  class="border bg-black border-black/40 rounded-11xl px-4 py-1.5 cursor-pointer"
                  @click="inviteMember(member.email)"
              >
                <img src="@/assets/icons/svg/Add_16x16_white.svg" :alt="t('select')" />
              </div>
              <!-- Cancel invitation -->
              <div v-else-if="invitees.find(inv => inv.email === member.email)"
                  class="border border-black/40 rounded-11xl px-4 py-1.5 cursor-pointer"
                   @click="removeMember(member.email)"
              >
                <img src="@/assets/icons/svg/Close_16x16.svg" :alt="t('remove')" />
              </div>
              <!-- If already in the crew list -->
              <div v-else
                   class="border border-black/40 rounded-11xl px-4 py-1.5 cursor-pointer"
              >
                <img src="@/assets/icons/svg/Check_16x16.svg" :alt="t('checked')" />
              </div>
            </div>
          </div>
          <hr class="mt-6">
        </div>
      </div>
    </div>
  </CustomModal>
</template>

<style scoped>
:deep(.modal-content) {
  padding: 0;
}
:deep(.v-input__details) {
  display: none !important;
}
:deep(.v-field__outline) {
  margin: 0 !important;
  padding: 0 !important;
}
:deep(.v-input__control) {
  margin-bottom: 0 !important;
}
.arrow-btn {
  position: relative;
}

.arrow-btn .icon-hover {
  display: none;
}

.arrow-btn:hover .icon-default {
  display: none;
}

.arrow-btn:hover .icon-hover {
  display: inline;
}
</style>
